// Resource limits (D83, docs/12 "Resource limits"), written by hand until the routes reach the
// generated schema (S129). A user's limit is their own override, else their role's, else the
// platform default; it is the size of their new environments and the ceiling of a resize.
import { queryOptions } from "@tanstack/react-query";

import { requestJson } from "../../api/http";

export const GIB = 1024 ** 3;

export interface Size {
  cpus: number;
  memory_bytes: number;
}

export interface RoleLimit extends Size {
  role_id: string;
  role_name: string;
}

export interface UserLimit extends Size {
  user_id: string;
  email: string;
  display_name: string;
}

export interface Floors {
  vm: Size;
  container: Size;
}

export interface AdminLimits {
  default: Size;
  roles: RoleLimit[];
  users: UserLimit[];
  host: Size;
  floor: Floors;
}

export type LimitSource = "default" | "role" | "user";

export interface MyLimits extends Size {
  source: LimitSource;
  host: Size;
  floor: Floors;
}

export type LimitsState = "applied" | "restarting" | "pending";

/** The parts of an `EnvironmentDto` the Resources card reads, with D83's `limits_state`. */
export interface EnvironmentLimits {
  id: string;
  status: string;
  limits_cpu: number;
  limits_memory_bytes: number;
  limits_state: LimitsState;
}

type Json = Record<string, unknown>;
const obj = (x: unknown): Json => (typeof x === "object" && x !== null ? (x as Json) : {});
const str = (x: unknown): string => (typeof x === "string" ? x : "");
const num = (x: unknown): number => (typeof x === "number" && Number.isFinite(x) ? x : 0);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);

export const parseSize = (x: unknown): Size => ({ cpus: num(obj(x).cpus), memory_bytes: num(obj(x).memory_bytes) });
const parseFloors = (x: unknown): Floors => ({ vm: parseSize(obj(x).vm), container: parseSize(obj(x).container) });

export function parseAdminLimits(x: unknown): AdminLimits {
  const l = obj(x);
  return {
    default: parseSize(l.default),
    roles: list(l.roles).map((r) => ({ ...parseSize(r), role_id: str(obj(r).role_id), role_name: str(obj(r).role_name) })),
    users: list(l.users).map((u) => ({
      ...parseSize(u),
      user_id: str(obj(u).user_id),
      email: str(obj(u).email),
      display_name: str(obj(u).display_name),
    })),
    host: parseSize(l.host),
    floor: parseFloors(l.floor),
  };
}

const SOURCES: readonly LimitSource[] = ["default", "role", "user"];
const STATES: readonly LimitsState[] = ["applied", "restarting", "pending"];

export function parseMyLimits(x: unknown): MyLimits {
  const l = obj(x);
  const source = SOURCES.find((s) => s === l.source) ?? "default";
  return { ...parseSize(l), source, host: parseSize(l.host), floor: parseFloors(l.floor) };
}

export function parseEnvironmentLimits(x: unknown): EnvironmentLimits {
  const e = obj(x);
  return {
    id: str(e.id),
    status: str(e.status),
    limits_cpu: num(e.limits_cpu),
    limits_memory_bytes: num(e.limits_memory_bytes),
    // An API from before D83 has no state: whatever it holds is applied.
    limits_state: STATES.find((s) => s === e.limits_state) ?? "applied",
  };
}

/** Whole GiB, rounded down: the unit the pages edit memory in. */
export const toGib = (bytes: number): number => Math.floor(bytes / GIB);
export const fromGib = (gib: number): number => gib * GIB;

/** The lowest limit an admin may set: one every isolation can be created at. */
export function limitFloor(floor: Floors): Size {
  return { cpus: Math.max(floor.vm.cpus, floor.container.cpus, 1), memory_bytes: Math.max(floor.vm.memory_bytes, floor.container.memory_bytes) };
}

const enc = encodeURIComponent;
const ADMIN = "/api/admin/resource-limits";
const body = (s: Size): Size => ({ cpus: s.cpus, memory_bytes: s.memory_bytes });

export const adminLimitsQuery = () =>
  queryOptions({ queryKey: ["resource-limits", "admin"], queryFn: async () => parseAdminLimits(await requestJson("GET", ADMIN)), retry: false });

export const myLimitsQuery = () =>
  queryOptions({ queryKey: ["resource-limits", "me"], queryFn: async () => parseMyLimits(await requestJson("GET", "/api/me/resource-limits")), retry: false });

/** Where an admin's write goes: the default, one role's override or one user's. */
export type LimitTarget = { scope: "default" } | { scope: "role"; id: string } | { scope: "user"; id: string };

function targetPath(t: LimitTarget): string {
  if (t.scope === "default") return `${ADMIN}/default`;
  return `${ADMIN}/${t.scope === "role" ? "roles" : "users"}/${enc(t.id)}`;
}

export async function setLimit(target: LimitTarget, size: Size): Promise<void> {
  await requestJson("PUT", targetPath(target), body(size));
}

export async function removeLimit(target: Exclude<LimitTarget, { scope: "default" }>): Promise<void> {
  await requestJson("DELETE", targetPath(target));
}

/** The environment as the group page's Resources card reads it (refetched with the group). */
export const environmentLimitsQuery = (groupId: string, envId: string) =>
  queryOptions({
    queryKey: ["task-groups", groupId, "environment", envId],
    queryFn: async () => parseEnvironmentLimits(await requestJson("GET", `/api/environments/${enc(envId)}`)),
  });

export async function resizeEnvironment(envId: string, size: Size): Promise<EnvironmentLimits> {
  return parseEnvironmentLimits(await requestJson("PUT", `/api/environments/${enc(envId)}/limits`, body(size)));
}
